import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChildren
} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {IAttribute, IAttributeTerm, IOption} from "../../add-product-form.interface";
import {IDsDropdownOption} from "../../../../../../../shared/components/ds-dropdown/ds-dropdown-option";
import {ProductAddHelperService} from "../../product-add-helper.service";
import {DragulaService} from "ng2-dragula";
import {DbModalService} from "../../../../../../../modules/modal/modal";
import {RemoveItemDialogComponent} from "../../../remove-item-dialog/remove-item-dialog.component";

@Component({
    selector: 'db-product-variants',
    templateUrl: './product-variants.component.html',
    styleUrl: './product-variants.component.scss',
    standalone: false
})
export class ProductVariantsComponent {
  @Input() variantsForm: FormGroup;
  @Input() activeAttributes: IOption[];
  @Input() showApply = true;
  @Input() instantDelete = false;
  @Output() apply: EventEmitter<void> = new EventEmitter<void>();
  @ViewChildren('formRow') rows: ElementRef;

  emptyForm: FormGroup = this._fb.group({
    option: this._fb.group({
      label: '',
      value: ''
    }),
    option_value: this._fb.array([]),
  });
  emptyOptionDropdownKeepValue = true;
  attributesWithTerms: IAttribute[] = [];
  terms: { [id: number]: IAttributeTerm[] } = {};
  filteredAttributes = [];

  @Input() set attributes(atts: IAttribute[]) {
    this.attributesWithTerms = atts;
    this._setTermsObj();
    this.allAttributes = atts?.map(att => ({label: att.name, value: att.id}));
    this._filterAttributes();
  }

  allAttributes: { label: string; value: number; disabled?: boolean }[] = [];
  @Output() openVariantsModal: EventEmitter<IOption[]> = new EventEmitter<IOption[]>();

  constructor(private _fb: FormBuilder,
              private _cdr: ChangeDetectorRef,
              private _renderer: Renderer2,
              private _elRef: ElementRef,
              private _dragulaService: DragulaService,
              private _dialogService: DbModalService,
              private _productAddHelper: ProductAddHelperService) {
    const group = this._dragulaService.find('PRODUCT_VARIANTS');
    if (!group) {
      this._dragulaService.createGroup('PRODUCT_VARIANTS', {
        revertOnSpill: true,
        direction: 'vertical',
        moves: (el, container, handle) => {
          return handle.classList.contains('handle');
        }
      });
    }

  }

  ngOnInit() {
  }

  private _setTermsObj() {
    this.attributesWithTerms.forEach(attribute => {
      this.terms[attribute.id] = attribute.terms;
    });
  }

  onRemoveItem(index: number) {
    if (!this.instantDelete) {
      this._openDeleteModal(index);
      return;
    }
    this._removeItem(index);
  }

  private _removeItem(index: number) {
    const item = this.variantsFormArray.at(index).get('option').getRawValue();
    if (item?.value > 1000000) {
      this.allAttributes = this.allAttributes.filter(att => att.value !== item.value)
    }
    this.variantsFormArray.removeAt(index);
    this._filterAttributes();
    this.apply.emit();
  }

  onEmptyAdd(value) {
    if (!value) {
      return;
    }
    const form = this._productAddHelper.variantFormTemplate();
    const options = this.emptyForm.get('option_value') as FormArray;
    form.get('option').setValue(this.emptyForm.get('option').value);
    options.controls.map((value, index) => {
      (form.get('option_value') as FormArray).push(this._fb.group({name: value.get('name').value}));
    });
    const optionValue = this.emptyForm.get('option').get('label').value;
    const existingAtt = this.allAttributes.some(att => att.label === optionValue);
    if (!existingAtt) {
      this.allAttributes.push({label: optionValue, value: this.emptyForm.get('option').get('value').value});
    }
    this.variantsFormArray.push(form);
    this._clearVariantsArray();
    this.emptyOptionDropdownKeepValue = false;
    setTimeout(() => {
      this.focusLastChipInput();
      this._cdr.detectChanges();
      this.emptyOptionDropdownKeepValue = true;
    });
    this._filterAttributes();
  }

  private _clearVariantsArray() {
    while (this.emptyOptionsFormArray.length !== 0) {
      this.emptyOptionsFormArray.removeAt(0);
    }
    this.emptyForm.get('option').reset();
  }

  get variantsFormArray(): FormArray {
    return this.variantsForm.get('variants') as FormArray;
  }

  get emptyOptionsFormArray(): FormArray {
    return this.emptyForm.get('option_value') as FormArray;
  }

  chooseOption({label, value, ...test}: IDsDropdownOption, formControl: FormControl) {
    if (value === null) {
      value = new Date().getTime();
      this.allAttributes.push({value: value as number, label, disabled: true});
    }
    formControl.get('option').setValue({value, label});
    this.variantsForm.markAsDirty();
    this._filterAttributes();
  }

  optionInputChange(value, formControl: FormControl) {
    if (!value) {
      const item = formControl.get('option').getRawValue()
      if (item?.value > 1000000) {
        this.allAttributes = this.allAttributes.filter(att => att.value !== item.value)
      }
      formControl.get('option').reset();
      this._filterAttributes();
    }
  }

  orderChanged(newOrder) {
    const formArray = this.variantsFormArray;
    // Clear the existing FormArray and add the reordered items
    formArray.clear();
    newOrder.forEach(control => formArray.push(control));
  }

  focusLastChipInput() {
    const inputs = this._elRef.nativeElement.querySelectorAll('.variants-chip .chip-input');
    if (inputs.length > 0) {
      const lastInput = inputs[inputs.length - 1];
      this._renderer.selectRootElement(lastInput).click();
      this._renderer.selectRootElement(lastInput).focus();
    }
  }

  onApply(item: FormControl) {
    item.markAsPristine();
    this.apply.emit();
  }

  private _filterAttributes() {
    const variations = this.variantsFormArray.getRawValue().map(variant => variant.option.value);
    this.filteredAttributes = this.allAttributes.map(value => {
      return {
        ...value,
        disabled: variations.includes(value.value) || value.disabled
      };
    });
  }

  private _openDeleteModal(index: number) {
    const modal = this._dialogService.open(RemoveItemDialogComponent);
    modal.componentInstance.alternativePopup = {
      title: 'Remove an option',
      description: 'Removing an option will reset all the values of your variations.',
      button: 'Remove'
    };

    modal.closed.subscribe(value => {
      if (value) {
        this._removeItem(index);
      }
    });
  }

}
